/** Minimal 2D vector helpers. Plain objects keep them cheap to allocate and log. */

export interface Vec2 {
  x: number;
  y: number;
}

export const vec2 = (x = 0, y = 0): Vec2 => ({ x, y });

export const set = (out: Vec2, x: number, y: number): Vec2 => {
  out.x = x;
  out.y = y;
  return out;
};

export const copy = (out: Vec2, a: Vec2): Vec2 => set(out, a.x, a.y);

export const add = (out: Vec2, a: Vec2, b: Vec2): Vec2 => set(out, a.x + b.x, a.y + b.y);

export const sub = (out: Vec2, a: Vec2, b: Vec2): Vec2 => set(out, a.x - b.x, a.y - b.y);

export const scale = (out: Vec2, a: Vec2, s: number): Vec2 => set(out, a.x * s, a.y * s);

export const addScaled = (out: Vec2, a: Vec2, b: Vec2, s: number): Vec2 =>
  set(out, a.x + b.x * s, a.y + b.y * s);

export const length = (a: Vec2): number => Math.hypot(a.x, a.y);

export const lengthSq = (a: Vec2): number => a.x * a.x + a.y * a.y;

export const distance = (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.y - b.y);

export const distanceSq = (a: Vec2, b: Vec2): number => {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
};

export const normalize = (out: Vec2, a: Vec2): Vec2 => {
  const len = Math.hypot(a.x, a.y);
  return len > 1e-9 ? set(out, a.x / len, a.y / len) : set(out, 0, 0);
};

export const clamp = (value: number, min: number, max: number): number =>
  value < min ? min : value > max ? max : value;

export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** Smoothstep, matching the GLSL builtin so shader and simulation agree. */
export const smoothstep = (edge0: number, edge1: number, x: number): number => {
  const t = clamp((x - edge0) / (edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
};
